import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, from, throwError } from 'rxjs';
import { delay, map, catchError, switchMap } from 'rxjs/operators';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import {
  PaginatedDropdownLoadParams,
  PaginatedOptionsPage,
} from '@shared/utils/paginated-dropdown.util';
import { formatDateToISO } from '@shared/utils/date-formatter.util';

import { 
  StockDeliveryRecord, 
  StockDeliveryResultRecord,
  StockDeliveryListResponse,
  StockDeliveryResultListResponse 
} from './stock-delivery.config';

// Import API models and service
import { 
  PageInfoOutboundDeliveryOrderListVO,
  OutboundDeliveryOrderListVO,
  OutboundDeliveryOrderInfoVO,
  ResultRecordControllerService,
  ResultRecordPageDTO,
  PageInfoResultRecordListVO,
  ResultRecordListVO,
  ResultRecordInfoVO
} from '@app/api';
import {
  WarehouseControllerService,
  WarehouseQueryReqDTO,
  WarehouseQueryResVO,
  OutboundDeliveryOrderService,
  OutboundDeliveryOrderPageDTO
} from '@app/api/stock-service';
import { OutboundDeliveryOrderStatusUpdateDTO } from '@app/api/stock-service/model/outboundDeliveryOrderStatusUpdateDTO';
import { OutboundDeliveryOrderCreateFromOrderDTO } from '@app/api/stock-service/model/outboundDeliveryOrderCreateFromOrderDTO';
import { OutboundDeliveryOrderDetailVO } from '@app/api/stock-service/model/outboundDeliveryOrderDetailVO';
import { OutboundDeliveryOrderShippingDTO } from '@app/api/stock-service/model/outboundDeliveryOrderShippingDTO';
import { OutboundDeliveryItem } from './stock-delivery.config';
import { mapStatusToString } from './delivery.helpers';

export interface StockDeliverySearchParams {
  queryCode?: string;
  outType?: string;
  channelTypeCode?: string;
  shopIdList?: string[];
  status?: string | number;
  outLogicWarehouseCode?: string;
  beginCreationDate?: string;
  endCreationDate?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  ascending?: boolean;
  orderId?: string | number;
  orderCode?: string;
}

export interface StockDeliveryResultSearchParams {
  queryCode?: string;
  channelTypeCode?: string;
  shopIdList?: string[];
  outLogicWarehouseCode?: string;
  beginCreationDate?: string;
  endCreationDate?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  ascending?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class StockDeliveryService {
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);

  constructor(
    private http: HttpClient,
    private outboundDeliveryOrderService: OutboundDeliveryOrderService,
    private resultRecordService: ResultRecordControllerService,
    private warehouseController: WarehouseControllerService
  ) {}

  // Real API integration for outbound delivery notification
  getDeliveryRecords(params: StockDeliverySearchParams): Observable<StockDeliveryListResponse> {
    // Clean up search params - remove null values and empty arrays
    const cleanParams = this.cleanSearchParams(params);
    
    // Convert search params to API DTO
    const apiParams: OutboundDeliveryOrderPageDTO = {
      page: cleanParams.page || 0,
      size: cleanParams.size || 20,
      sortBy: cleanParams.sortBy || 'creationDate',
      ascending: cleanParams.ascending !== undefined ? cleanParams.ascending : false,
      queryCode: cleanParams.queryCode,
      channelTypeCode: cleanParams.channelTypeCode,
      shopIdList: cleanParams.shopIdList && cleanParams.shopIdList.length > 0 
        ? cleanParams.shopIdList.map((id: string) => parseInt(id, 10)).filter((id: number) => !isNaN(id))
        : undefined,
      status: cleanParams.status !== undefined && cleanParams.status !== null && cleanParams.status !== '' 
        ? [typeof cleanParams.status === 'number' ? cleanParams.status : parseInt(String(cleanParams.status), 10)]
        : undefined,
      outType: cleanParams.outType,
      outLogicWarehouseCode: cleanParams.outLogicWarehouseCode,
      beginCreationDate: cleanParams.beginCreationDate,
      endCreationDate: cleanParams.endCreationDate,
      orderId: cleanParams.orderId ? parseInt(String(cleanParams.orderId), 10) : undefined,
      orderCode: cleanParams.orderCode,
    };

    return this.outboundDeliveryOrderService.page4(apiParams).pipe(
      switchMap((response: any) => {
        // Handle blob response
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      map((response: PageInfoOutboundDeliveryOrderListVO) => {
        // Convert API response to component format
        const pageContent = BlobConverterUtil.extractPageContent(response, []);
        const totalElements = BlobConverterUtil.extractTotalElements(response, 0);
        
        const data: StockDeliveryRecord[] = pageContent.map((item: OutboundDeliveryOrderListVO) => 
          this.mapApiToComponentRecord(item)
        );

        return {
          data,
          totalRecords: totalElements,
          loading: false
        };
      }),
      catchError(error => {
        // Return empty response on error
        return of({
          data: [],
          totalRecords: 0,
          loading: false
        });
      })
    );
  }


  // Real API integration for outbound delivery result
  getDeliveryResultRecords(params: StockDeliveryResultSearchParams): Observable<StockDeliveryResultListResponse> {
    // Format dates to ISO string BEFORE cleaning (to avoid Date object serialization issues)
    const beginCreationDateISO = formatDateToISO(params.beginCreationDate, false);
    const endCreationDateISO = formatDateToISO(params.endCreationDate, true);
    
    // Create params with formatted dates
    const paramsWithFormattedDates = {
      ...params,
      beginCreationDate: beginCreationDateISO,
      endCreationDate: endCreationDateISO,
    };
    
    // Clean up search params (similar to getDeliveryRecords)
    const cleanParams = this.cleanSearchParams(paramsWithFormattedDates);
    
    // Convert search params to API DTO
    const apiParams: ResultRecordPageDTO = {
      page: cleanParams.page || 0,
      size: cleanParams.size || 20,
      queryCode: cleanParams.queryCode || undefined,
      channelTypeCode: cleanParams.channelTypeCode || undefined,
      shopIdList: cleanParams.shopIdList && cleanParams.shopIdList.length > 0 ? cleanParams.shopIdList.map((id: string) => parseInt(id)) : undefined,
      outLogicWarehouseCode: cleanParams.outLogicWarehouseCode || undefined,
      beginCreationDate: cleanParams.beginCreationDate,
      endCreationDate: cleanParams.endCreationDate,
    };

    // Only add sortBy and ascending if sortBy is provided and is a string (not a Date object)
    if (cleanParams.sortBy && typeof cleanParams.sortBy === 'string' && cleanParams.sortBy.trim() !== '') {
      apiParams.sortBy = cleanParams.sortBy;
      apiParams.ascending = cleanParams.ascending !== undefined ? cleanParams.ascending : true;
    }

    // Call API POST /v1/result-records/page
    return this.resultRecordService.page3(apiParams).pipe(
      switchMap((response: any) => {
        // Handle blob response
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      map((response: PageInfoResultRecordListVO) => {
        // Extract page content and total elements using BlobConverterUtil
        const pageContent = BlobConverterUtil.extractPageContent(response, []);
        const totalElements = BlobConverterUtil.extractTotalElements(response, 0);
        
        // Convert API response to component format
        const data: StockDeliveryResultRecord[] = pageContent.map((item: ResultRecordListVO) => 
          this.mapApiToComponentResultRecord(item)
        );

        return {
          data,
          totalRecords: totalElements,
          loading: false
        };
      }),
      catchError(error => {
        console.error('Error fetching delivery result records:', error);
        // Return empty response on error
        return of({
          data: [],
          totalRecords: 0,
          loading: false
        });
      })
    );
  }

  // Get delivery details by ID
  getDeliveryDetail(id: string): Observable<StockDeliveryRecord> {
    const numericId = parseInt(id, 10);
    if (isNaN(numericId)) {
      return throwError(() => new Error('Invalid ID'));
    }

    // Call real API: /v1/outbound/delivery/order/detail/{id}
    return this.outboundDeliveryOrderService.detail7(numericId).pipe(
      switchMap((response: any) => {
        // Handle blob response
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      map((apiItem: OutboundDeliveryOrderInfoVO) => {
        // Map API response to component record format
        return this.mapApiInfoToComponentRecord(apiItem);
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  // Get delivery order details (product items) by ID
  getDeliveryOrderDetails(id: string): Observable<OutboundDeliveryItem[]> {
    const numericId = parseInt(id, 10);
    if (isNaN(numericId)) {
      return throwError(() => new Error('Invalid ID'));
    }

    // Call real API: /v1/outbound/delivery/order/{id}/details
    return this.outboundDeliveryOrderService.getOdoDetails(numericId).pipe(
      switchMap((response: any) => {
        // Handle blob response
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      map((apiItems: OutboundDeliveryOrderDetailVO[]) => {
        // Map API response to component format
        // Note: API returns OutboundDeliveryOrderDetailVO which has pickedQty field
        // System supports multiple picks and multiple shipments:
        // - plannedQty: SL lúc đầu lên kế hoạch sẽ xuất
        // - pickedQty: SL hàng đã lấy (trong page Lấy hàng)
        // - alreadyOutQty: SL hàng đã được xuất kho (sau confirm shipping)
        // - waitOutQty: SL đang chờ xuất
        const mappedItems = apiItems.map((item: OutboundDeliveryOrderDetailVO) => {
          const plan = item.plannedQty || 0;
          // API returns pickedQty directly (Quantity to be picked / SL hàng đã lấy)
          const apiPickedQty = item.pickedQty !== undefined && item.pickedQty !== null ? item.pickedQty : 0;
          // API alreadyOutQty: "Quantity shipped" (SL hàng đã được xuất kho sau confirm shipping)
          const apiAlreadyOutQty = item.alreadyOutQty || 0;
          // waitOutQty: SL đang chờ xuất = plannedQty - pickedQty
          const waitOutQty = item.waitOutQty !== undefined ? item.waitOutQty : (plan - apiPickedQty);
          
          return {
            id: item.id?.toString(),
            serialNumber: 0, // Will be set by component
            spuCode: item.spuCode || '',
            spuName: item.spuName || '',
            skuCode: item.skuCode || '',
            skuName: item.skuName || '',
            quantity: plan,
            plannedQty: plan,
            pickedQty: apiPickedQty, // SL hàng đã lấy
            alreadyOutQty: apiAlreadyOutQty, // SL hàng đã được xuất kho
            waitOutQty: waitOutQty, // SL đang chờ xuất
            batchId: item.batchId,
          };
        });
        
        return mappedItems;
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  // Helper method to map API InfoVO response to component record
  private mapApiInfoToComponentRecord(apiItem: OutboundDeliveryOrderInfoVO): StockDeliveryRecord {
    return {
      id: apiItem.id?.toString() || '',
      code: apiItem.code || '',
      platformOrderCode: apiItem.platformOrderCode || '',
      orderCode: apiItem.orderCode || '',
      frontRecordCode: apiItem.frontRecordCode || '',
      frontRecordId: (apiItem as any).frontRecordId?.toString(),
      outType: apiItem.outType || '',
      outTypeMeaning: apiItem.outTypeMeaning || '',
      status: mapStatusToString(apiItem.status),
      qty: (apiItem as any).qty || 0,
      outLogicWarehouseCode: apiItem.outLogicWarehouseCode || '',
      outLogicWarehouseName: apiItem.outLogicWarehouseName || '',
      receivingLogicWarehouseCode: apiItem.receivingLogicWarehouseCode || '',
      receivingLogicWarehouseName: apiItem.receivingLogicWarehouseName || '',
      shopName: apiItem.shopName || '',
      channelTypeCodeMeaning: (apiItem as any).channelTypeCodeMeaning || '',
      creationDate: apiItem.creationDate || '',
      logisticCompany: apiItem.logisticCompany || '',
      logisticBusiness: apiItem.logisticBusiness || '',
      scheduledDeliveryTime: apiItem.scheduledDeliveryTime || undefined,
      receiver: apiItem.receiverName || '',
      // Map items if available
      detailList: (apiItem as any).detailList || (apiItem as any).items || [],
    };
  }


  // Get delivery result details by ID
  getDeliveryResultDetail(id: string): Observable<StockDeliveryResultRecord> {
    const numericId = parseInt(id);
    if (isNaN(numericId)) {
      return of(null as any);
    }

    return this.resultRecordService.detail3(numericId).pipe(
      switchMap((response: any) => {
        // Handle blob response by converting it to JSON
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      map((response: ResultRecordInfoVO) => {
        return this.mapApiToComponentResultRecord(response);
      }),
      catchError(error => {
        console.error('Error fetching delivery result detail:', error);
        return of(null as any);
      })
    );
  }

  // Helper method to map API response to component record
  private mapApiToComponentRecord(apiItem: OutboundDeliveryOrderListVO): StockDeliveryRecord {
    return {
      id: apiItem.id?.toString() || '',
      code: apiItem.code || '',
      platformOrderCode: apiItem.platformOrderCode || '',
      orderCode: apiItem.orderCode || '',
      frontRecordCode: apiItem.frontRecordCode || '',
      frontRecordId: apiItem.frontRecordId,
      outType: apiItem.outType || '',
      outTypeMeaning: apiItem.outTypeMeaning || '',
      status: mapStatusToString(apiItem.status),
      qty: apiItem.qty || 0,
      outLogicWarehouseCode: apiItem.outLogicWarehouseCode || '',
      outLogicWarehouseName: apiItem.outLogicWarehouseName || '',
      receivingLogicWarehouseCode: (apiItem as any).receivingLogicWarehouseCode || '',
      receivingLogicWarehouseName: (apiItem as any).receivingLogicWarehouseName || '',
      shopName: apiItem.shopName || '',
      channelTypeCodeMeaning: apiItem.channelTypeCodeMeaning || '',
      creationDate: apiItem.creationDate || '',
      logisticCompany: (apiItem as any).logisticCompany || '',
      logisticBusiness: (apiItem as any).logisticBusiness || '',
      alreadyQty: apiItem.alreadyQty || 0,
      receiver: (apiItem as any).receiver || '',
    };
  }

  // Helper method to map API response to component result record
  private mapApiToComponentResultRecord(apiItem: ResultRecordListVO | ResultRecordInfoVO): StockDeliveryResultRecord {
    // Check if it's a list item (has alreadyOutQty) or detail item
    const isListItem = 'alreadyOutQty' in apiItem;
    const statusStr = mapStatusToString(apiItem.status);
    
    return {
      id: apiItem.id?.toString() || '',
      resultCode: apiItem.resultCode || '',
      odoCode: apiItem.odoCode || '',
      odoId: (apiItem as any).odoId?.toString() || '',
      orderCode: apiItem.orderCode || '',
      outType: apiItem.outType || '', // outType code from API: 'transfer-out-stock' or 'order-sales-out-stock'
      outTypeMeaning: apiItem.outTypeMeaning || '',
      status: statusStr,
      alreadyOutQty: isListItem ? (apiItem as ResultRecordListVO).alreadyOutQty || 0 : 0, // Only available in list
      outLogicWarehouseCode: apiItem.outLogicWarehouseCode || '',
      outLogicWarehouseName: apiItem.outLogicWarehouseName || '',
      shopName: apiItem.shopName || '',
      channelTypeCodeMeaning: apiItem.channelTypeCodeMeaning || '',
      creationDate: apiItem.creationDate || ''
    };
  }

  // Helper method to clean search parameters
  private cleanSearchParams(params: any): any {
    const cleanParams: any = {};
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      
      // Skip null, undefined, empty strings, and empty arrays
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          // Only include non-empty arrays
          if (value.length > 0) {
            cleanParams[key] = value;
          }
        } else {
          cleanParams[key] = value;
        }
      }
    });
    
    return cleanParams;
  }


  // Get outbound status options
  // Status mapping from API:
  // 0 Waiting to notify WMS -> PENDING
  // 1 Waiting to ship -> APPROVED + PICKING
  // 2 Failed to notify WMS -> REJECT (bỏ qua)
  // 3 Fully shipped -> SHIPPED
  // 4 Partially shipped -> SHIPPED
  // 5 Canceled -> CANCELLED
  // 6 Picked -> PICKED
  getOutboundStatusOptions(): Observable<any[]> {
    const options = [
      { label: 'outbound_management.status.pending', value: 0 }, // Waiting to notify WMS
      { label: 'outbound_management.status.approved', value: 1 }, // Waiting to ship -> APPROVED + PICKING
      { label: 'outbound_management.status.picked', value: 6 }, // All items picked
      { label: 'outbound_management.status.shipped', value: 3 }, // Fully shipped
      { label: 'outbound_management.status.partially_shipped', value: 4 }, // Partially shipped
      { label: 'outbound_management.status.cancelled', value: 5 } // Canceled
      // Note: Status 2 (REJECT) not included in filter options
    ];
    return of(options).pipe(delay(200));
  }

  // Get outbound type options
  getOutboundTypeOptions(): Observable<any[]> {
    const options = [
      { label: 'outbound_management.outbound_type.transfer_order', value: 'TRANSFER_ORDER' },
      { label: 'outbound_management.outbound_type.sales_order', value: 'SALES_ORDER' }
    ];
    return of(options).pipe(delay(200));
  }

  getWarehouseOptionsPage(
    params: PaginatedDropdownLoadParams,
  ): Observable<PaginatedOptionsPage<string | number>> {
    return this.warehouseOptionsService.getOptionsPage(params, {
      status: true,
      typeCodes: ['MAIN', 'STORE'],
      valueKey: 'code',
    });
  }

  // Get warehouse options from API
  getWarehouseOptions(): Observable<any[]> {
    const queryDTO: WarehouseQueryReqDTO = {
      page: 0,
      size: 100,
      status: true,
      typeCodes: ['MAIN', 'STORE'],
    };

    return this.warehouseController.queryWarehousePage(queryDTO).pipe(
      switchMap((response: any) => {
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      map((response: any) => {
        const pageContent = BlobConverterUtil.extractPageContent(response, []);
        const warehouses = pageContent as WarehouseQueryResVO[];
        
        // Map to { label, value } format
        return warehouses.map((warehouse: WarehouseQueryResVO) => ({
          label: warehouse.name || warehouse.code || '',
          value: warehouse.code || '',
        }));
      }),
      catchError((error) => {
        return of([]);
      })
    );
  }

  // Update outbound delivery order status
  // Map string status to number for API
  updateOutboundStatus(id: string, status: string): Observable<any> {
    const numericId = parseInt(id, 10);
    if (isNaN(numericId)) {
      return throwError(() => new Error('Invalid ID'));
    }

    // Map string status to number
    // Status mapping to API:
    // PENDING -> 0 (Waiting to notify WMS)
    // APPROVED/PICKING -> 1 (Waiting to ship)
    // REJECT -> 2 (Failed to notify WMS)
    // SHIPPED -> 3 (Fully shipped)
    // PARTIALLY_SHIPPED -> 4 (Partially shipped)
    // CANCELLED -> 5 (Canceled)
    const statusMap: { [key: string]: number } = {
      'PENDING': 0,
      'CREATED': 0, // Legacy support
      'APPROVED': 1, // Waiting to ship
      'PICKING': 1, // PICKING maps to 1 (Waiting to ship)
      'READY_TO_SHIP': 1, // READY_TO_SHIP maps to 1
      'SHIPPED': 3, // Fully shipped -> API 3
      'PARTIALLY_SHIPPED': 4, // Partially shipped -> API 4
      'REJECT': 2,
      'REJECTED': 2, // Legacy support
      'CANCELED': 5,
      'CANCELLED': 5, // Legacy support
    };

    const statusNumber = statusMap[status];
    if (statusNumber === undefined) {
      return throwError(() => new Error(`Invalid status: ${status}`));
    }

    const updateDTO: OutboundDeliveryOrderStatusUpdateDTO = {
      status: statusNumber,
      reason: status === 'CANCELLED' || status === 'CANCELED' || status === 'REJECT' 
        ? 'Rejected by user' 
        : undefined,
    };

    return this.outboundDeliveryOrderService.updateStatus2(numericId, updateDTO).pipe(
      switchMap((response: any) => {
        // Handle blob response
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      catchError((error: any) => {
        if (error?.error && BlobConverterUtil.isBlob(error.error)) {
          return from(BlobConverterUtil.convertToJson(error.error)).pipe(
            catchError(() => throwError(() => error)),
            switchMap((errorBody: any) =>
              throwError(() => ({
                ...error,
                error: errorBody,
              })),
            ),
          );
        }
        return throwError(() => error);
      })
    );
  }

  // Create outbound delivery order from order
  createOutboundDeliveryOrder(data: OutboundDeliveryOrderCreateFromOrderDTO): Observable<any> {
    return this.outboundDeliveryOrderService.createFromOrder(data).pipe(
      switchMap((response: any) => {
        // Handle blob response
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  // Confirm shipping for outbound delivery order
  confirmShipping(id: string, realNoticeCode?: string, deliveryDate?: string): Observable<any> {
    const numericId = parseInt(id, 10);
    if (isNaN(numericId)) {
      return throwError(() => new Error('Invalid ID'));
    }

    const shippingDTO: OutboundDeliveryOrderShippingDTO = {
      outboundDeliveryOrderId: numericId,
      realNoticeCode: realNoticeCode,
      deliveryDate: deliveryDate,
    };

    return this.outboundDeliveryOrderService.confirmShipping(shippingDTO, 'body', false, {
      httpHeaderAccept: 'application/json' as any,
    }).pipe(
      switchMap((response: any) => {
        // Handle blob response
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      catchError((error: any) => {
        // Parse blob error body so UI can show CONCURRENT_MODIFICATION details (RBA-3132)
        if (error?.error && BlobConverterUtil.isBlob(error.error)) {
          return from(BlobConverterUtil.convertToJson(error.error)).pipe(
            catchError(() => throwError(() => error)),
            switchMap((errorBody: any) =>
              throwError(() => ({
                ...error,
                error: errorBody,
              })),
            ),
          );
        }
        return throwError(() => error);
      })
    );
  }

  // Get picking status for outbound delivery order
  // GET /v1/outbound/delivery/order/{id}/picking-status
  // Returns: { status: 'NOT_PICKED' | 'PARTIALLY_PICKED' | 'ALL_PICKED' }
  getPickingStatus(id: string): Observable<{ status: string }> {
    const numericId = parseInt(id, 10);
    if (isNaN(numericId)) {
      return throwError(() => new Error('Invalid ID'));
    }

    // Use the service's httpClient directly via outboundDeliveryOrderService
    // Similar to how confirmPicking works, but using GET request
    const configuration = (this.outboundDeliveryOrderService as any).configuration;
    if (!configuration) {
      return throwError(() => new Error('Configuration not available'));
    }

    const basePath = configuration.basePath || '';
    const url = `${basePath}/v1/outbound/delivery/order/${numericId}/picking-status`;

    // Get default headers and add authentication
    let headers = configuration.defaultHeaders || new HttpHeaders();
    headers = configuration.addCredentialToHeaders('BearerAuthentication', 'Authorization', headers, 'Bearer ');

    // Set Accept header
    const httpHeaderAcceptSelected = configuration.selectHeaderAccept(['*/*']);
    if (httpHeaderAcceptSelected) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.http.get<{ status: string }>(url, {
      headers,
    }).pipe(
      switchMap((response: any) => {
        // Handle blob response
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      catchError((error) => {
        console.error('Error getting picking status:', error);
        return throwError(() => error);
      })
    );
  }
}
